<script lang="ts">
    import type {Fond, RecordEditation} from 'src/typings';
    import {getLocalization, getLogger} from 'src/core';
    import type {ForceSetting} from 'shared/value-editors/kp-value-editor-force-settings/types';
    import type {RecordEditationManager} from '../record-editation.manager';
    import {assertExists, exists} from 'shared/utils/custom-utils';
    import {onMount} from 'svelte';
    import {ValueEditorSize} from 'shared/value-editors/types';
    import {isEqual} from 'lodash-es';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpValueEditorForceSettings from 'shared/value-editors/kp-value-editor-force-settings/KpValueEditorForceSettings.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpValueEditor from 'shared/value-editors/kp-value-editor/KpValueEditor.svelte';
    import RecordEditationHeading from './RecordEditationHeading.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import KpSearchBar from 'shared/ui-widgets/search/KpSearchBar.svelte';
    import KpHeading from 'shared/components/kp-heading/KpHeading.svelte';
    import KpDropdownMenuWrapper from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuWrapper.svelte';
    import KpDropdownMenuItem from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuItem.svelte';
    import {stringifyArgument} from 'shared/utils/error-utils';

    export let editationState: RecordEditation;
    export let editationManager: RecordEditationManager;
    export let viewType: 'modal' | 'page';
    export let isRequestPending: boolean;
    export let forceSearchResetButton: boolean;
    export let errorMessages: string[];

    const localize = getLocalization();
    const logger = getLogger();

    $: anyChanges = exists(editationState) && !isEqual(editationState, editationManager.editationObjectBeforeCurrentEditation);
    let mainMenuHeight = 0;

    $: logger.debug(`Record editation - anyChanges: ${anyChanges}`);
    $: logger.debug(`Record editation - isRequestPending: ${isRequestPending}`);

    onMount(() => {
        const mainMenuElement = document.querySelector('nav.kp-main-menu');

        if (exists(mainMenuElement)) {
            mainMenuHeight = mainMenuElement.getBoundingClientRect().height;
        }
    });

    const handleFondChange = (event: CustomEvent<Fond>) => {
        editationManager.actions.setFond(event.detail);
    };

    function createFondSelectorForceSettings(): ForceSetting[] {
        return [
            {type: 'single-acceptable', options: {switchToInlineModeThreshold: 1}},
            {type: 'multiple-acceptable', options: {switchToInlineModeThreshold: 1}}
        ];
    }

    function publishAndClose() {
        try {
            logger.debug('Record editation - publishAndClose');
            editationManager.callbacks.publishAndClose();
        } catch (e) {
            logger.error(`Record editation - publishAndClose [ERROR]: ${stringifyArgument(e)}`, e);
            throw e;
        }
    }

    function deleteDraftAndClose() {
        try {
            logger.debug('Record editation - deleteDraftAndClose');
            editationManager.callbacks.deleteDraftAndClose();
        } catch (e) {
            logger.error(`Record editation - deleteDraftAndClose [ERROR]: ${stringifyArgument(e)}`, e);
            throw e;
        }

    }

    function saveAndClose() {
        try {
            logger.debug('Record editation - saveAndClose');
            editationManager.callbacks.saveAndClose();
        } catch (e) {
            logger.error(`Record editation - saveAndClose [ERROR]: ${stringifyArgument(e)}`, e);
            throw e;
        }
    }

    function saveAndContinue() {
        assertExists(editationManager.callbacks.saveAndContinue);
        try {
            logger.debug('Record editation - saveAndContinue');
            editationManager.callbacks.saveAndContinue();
        } catch (e) {
            logger.error(`Record editation - saveAndContinue [ERROR]: ${stringifyArgument(e)}`, e);
            throw e;
        }
    }

    function discardAndClose() {
        try {
            logger.debug('Record editation - discardAndClose');
            editationManager.callbacks.discardAndClose();
        } catch (e) {
            logger.error(`Record editation - discardAndClose [ERROR]: ${stringifyArgument(e)}`, e);
            throw e;
        }
    }
</script>

<div class="record-editation-toolbar in-{viewType}" style:--main-menu-height="{mainMenuHeight}px">
    {#if viewType === 'page'}
        <div class="container">
            <KpHeading type="h1">
                <RecordEditationHeading {editationState}/>
            </KpHeading>
        </div>
    {/if}

    <div class="toolbar-content-wrapper" class:container={viewType === 'page'}>
        <div class="toolbar-content-container">
            <KpSearchBar width="auto"
                         height="34px"
                         forceResetButton={forceSearchResetButton}
                         placeholder={localize(/* @kp-localization localization.EnterPartOfTextOrCode */ 'localization.EnterPartOfTextOrCode')}
                         on:search
                         on:reset/>

            <div class="buttons-container">
                {#if editationState.draft}
                    <KpButton buttonStyle="success"
                              isDisabled={isRequestPending}
                              id="record-editation-publish-and-close-{editationManager.recordId}"
                              dataQa="record-editation-publish-and-close"
                              on:click={publishAndClose}>

                        <IconedContent icon="check">
                            {localize(/* @kp-localization editace.PublishAndClose */ 'editace.PublishAndClose')}
                        </IconedContent>
                    </KpButton>

                    <KpButton buttonStyle="danger"
                              isDisabled={isRequestPending}
                              id="record-editation-discard-concept-{editationManager.recordId}"
                              dataQa="record-editation-discard-concept"
                              on:click={deleteDraftAndClose}>

                        <IconedContent icon="trash">
                            {localize(/* @kp-localization editace.DiscardConcept */ 'editace.DiscardConcept')}
                        </IconedContent>
                    </KpButton>
                {/if}

                {#if !editationState.draft}
                    <KpButton buttonStyle="success"
                              isDisabled={isRequestPending}
                              id="record-editation-save-and-close-{editationManager.recordId}"
                              dataQa="record-editation-save-and-close"
                              on:click={saveAndClose}>

                        <IconedContent icon="disk">
                            {localize(/* @kp-localization editace.SaveAndClose */ 'editace.SaveAndClose')}
                        </IconedContent>
                    </KpButton>
                {/if}

                {#if exists(editationManager.callbacks.saveAndContinue)}
                    <KpButton isDisabled={isRequestPending}
                              id="record-editation-save-and-continue-{editationManager.recordId}"
                              dataQa="record-editation-save-and-continue"
                              on:click={saveAndContinue}>

                        <IconedContent icon="disk">
                            {localize(/* @kp-localization editace.SaveChangesAndContinue */ 'editace.SaveChangesAndContinue')}
                        </IconedContent>
                    </KpButton>
                {/if}

                {#if !editationState.draft}
                    <KpButton isDisabled={isRequestPending}
                              id="record-editation-close-{editationManager.recordId}"
                              dataQa="record-editation-close"
                              on:click={discardAndClose}>

                        <IconedContent icon="cross-circle">
                            {localize(/* @kp-localization commons.zavrit */ 'commons.zavrit')}
                        </IconedContent>
                    </KpButton>
                {/if}

                {#if !anyChanges && editationState.revisionSaved}
                    <div class="changes-saved-container">
                        <IconedContent icon="check-circle" gap="3px">
                            {localize(/* @kp-localization editace.AllChangesSaved */ 'editace.AllChangesSaved')}
                        </IconedContent>
                    </div>
                {/if}

                {#if errorMessages.length > 0}
                    <KpDropdownMenuWrapper>
                        <span class="error-messages-icon" slot="button">
                            <IconedContent icon="exclamation">
                                {localize(/* @kp-localization editace.ErrorsInEditation */ 'editace.ErrorsInEditation')}: {errorMessages.length}
                            </IconedContent>
                        </span>

                        <svelte:fragment slot="menu">
                            {#each errorMessages as errorMessage, index}
                                <KpDropdownMenuItem on:click={() => navigator.clipboard.writeText(errorMessage)}>
                                    {index + 1}. - {errorMessage}
                                </KpDropdownMenuItem>
                            {/each}
                        </svelte:fragment>
                    </KpDropdownMenuWrapper>
                {/if}

                <div class="loading-container" class:is-visible={isRequestPending}>
                    <KpLoadingBlock size="xs" data-qa="record-editation-processing-indicator"/>
                </div>
            </div>

            {#if exists(editationState.fondSelectionEditor)}
                <div class="fond-select-container">
                    <label class="text-muted" for="record-editation-fond-select">
                        {localize(/* @kp-localization editace.vyberFond */ 'editace.vyberFond')}:
                    </label>

                    <KpValueEditorForceSettings forceSettings={createFondSelectorForceSettings()}>
                        <KpValueEditor {...editationState.fondSelectionEditor}
                                       size={ValueEditorSize.SM}
                                       model={editationState.fond}
                                       editorId="record-editation-fond-select"
                                       on:model-change={handleFondChange}/>
                    </KpValueEditorForceSettings>
                </div>
            {/if}
        </div>
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro.media-queries.less";

    .record-editation-toolbar {
        width: 100%;
        display: flex;
        flex-direction: column;
        background-color: @themed-body-bg;
        gap: @spacing-sm;

        &.in-modal {
            padding: @spacing-ml;
            border-bottom: 1px solid @themed-border-default;

            @media (min-width: @screen-sm-min) {
                position: sticky;
                z-index: 90;
                top: 0;
            }
        }

        &.in-page {
            padding: @spacing-xl 0 @spacing-l;
            border-bottom: 1px solid @themed-border-default;

            @media (min-width: @screen-sm-min) {
                position: sticky;
                z-index: 90;
                top: var(--main-menu-height);
            }
        }

        .toolbar-content-wrapper .toolbar-content-container {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: @spacing-m;
            width: 100%;

            .buttons-container {
                display: flex;
                align-items: center;
                gap: @spacing-sm;

                .changes-saved-container {
                    color: var(--success-green);
                }

                .error-messages-icon {
                    color: var(--danger-red);
                }

                .loading-container {
                    opacity: 0;
                    visibility: hidden;
                    transition: visibility 0.2s ease-in-out, opacity 0.2s ease-in-out;

                    &.is-visible {
                        opacity: 1;
                        visibility: visible;
                    }
                }
            }

            .fond-select-container {
                display: flex;
                gap: @spacing-xs;
                margin-left: auto;
                flex-direction: column;

                @media (max-width: @screen-xs-max) {
                    margin-left: initial;
                }

                label {
                    padding: 0;
                    margin: 0;
                }
            }
        }
    }
</style>